import { useQuery } from '@tanstack/react-query';
import { endpoints } from '../api/endpoints';

interface Named {
  id: string;
  code: string;
  name?: string;
  is_active?: boolean;
  metal_id?: string;
  /** Parts of pure metal per gram — 0.916 for 916. Used to price on fine weight. */
  fineness?: string;
}

/**
 * The reference data every entry screen needs. Loaded once and cached, because
 * a shop's metals and purities change about once a year and a till reloading
 * them on every keystroke is wasted time at the counter.
 */
export function useMasters() {
  const metals = useQuery({ queryKey: ['metals'], queryFn: () => endpoints.masters.metals(), staleTime: 300_000 });
  const purities = useQuery({
    queryKey: ['purities'],
    queryFn: () => endpoints.masters.purities(),
    staleTime: 300_000,
  });
  // Short-lived, unlike metals: these are often added in another tab from the
  // "＋ New" link beside the dropdown, and should appear on coming back.
  const taxGroups = useQuery({
    queryKey: ['taxGroups'],
    queryFn: () => endpoints.masters.taxGroups(),
    staleTime: 10_000,
  });
  const pricingRules = useQuery({
    queryKey: ['pricingRules'],
    queryFn: () => endpoints.masters.pricingRules(),
    staleTime: 10_000,
  });

  const metalList = (metals.data?.data ?? []) as unknown as Named[];
  const purityList = (purities.data?.data ?? []) as unknown as Named[];

  return {
    isLoading: metals.isLoading || purities.isLoading,
    metals: metalList,
    purities: purityList,
    // Retired entries stay readable on old records but are not offered anew.
    taxGroups: ((taxGroups.data?.data ?? []) as unknown as Named[]).filter((t) => t.is_active !== false),
    pricingRules: ((pricingRules.data?.data ?? []) as unknown as Named[]).filter((r) => r.is_active !== false),
    /** Purities belong to one metal; offering 925 for gold is a data-entry trap. */
    // Hidden (deleted but in use) metals and purities still name themselves on
    // old records through `metals` and `purities`, but are never offered anew.
    puritiesFor: (metalId: string): Named[] =>
      purityList.filter((p) => p.metal_id === metalId && p.is_active !== false),
    metalOptions: metalList
      .filter((m) => m.is_active !== false)
      .map((m) => ({ value: m.id, label: `${m.code} — ${m.name ?? ''}`.trim() })),
    activeMetals: metalList.filter((m) => m.is_active !== false),
  };
}
